/**
 * 새 자산이 나왔는지 조용히 확인하는 자리.
 *
 * 홈 화면에서 연 앱은 창이 오래 산다. 다른 앱에 다녀와도 띄워 두었던 화면을 그대로 되살리므로
 * 문서를 다시 받는 순간이 오지 않는다. 그 창이 스스로 물어보게 하는 것이 이 파일이다.
 *
 * 확인과 다시 부르기를 갈라 둔다. 여기서는 확인만 하고 화면에 아무것도 하지 않는다. 화면을 다시
 * 세우는 일은 사용자가 버튼을 누를 때만 일어난다.
 */

/** 마지막 확인에서 이 시간이 지나지 않았으면 건너뛴다. 앞뒤로 자주 오가도 확인이 잦아지지 않는다. */
const MIN_INTERVAL_MS = 60_000

/**
 * 새 자산이 준비됐는지 지켜본다. 준비되면 한 번 알리고 그 뒤로는 부르지 않는다.
 *
 * 서비스 워커가 새로 서는 것을 새 자산의 신호로 삼는다. 워커 파일에는 자산 이름이 들어 있지
 * 않지만 배포가 워커까지 함께 갈아 끼우므로 둘이 같이 움직인다.
 *
 * 이미 워커가 이 창을 몰고 있을 때만 새로 선 워커를 새 자산으로 본다. 처음 설치되는 워커는
 * 이 창이 방금 받은 자산의 것이라 갱신이 아니다. 그 경우까지 알리면 앱을 처음 연 사람이
 * 다시 열라는 말을 듣는다.
 *
 * 돌려주는 함수를 부르면 지켜보기를 그만둔다.
 */
export function watchForUpdate(onReady: () => void): () => void {
  if (!('serviceWorker' in navigator)) return () => {}

  let stopped = false
  let lastCheckedAt = 0

  const announce = () => {
    if (stopped) return
    stopped = true
    onReady()
  }

  /**
   * 새로 들어온 워커가 설 때까지 지켜본다.
   *
   * installing에서 곧바로 알리지 않는다. 받다가 끊기면 새 자산이 없는데 알린 것이 된다.
   * installed까지 가야 이 창이 다시 서면 새 자산으로 그려진다는 것이 확실해진다.
   */
  const watchWorker = (worker: ServiceWorker | null) => {
    if (!worker || !navigator.serviceWorker.controller) return
    if (worker.state === 'installed' || worker.state === 'activated') {
      announce()
      return
    }
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed' || worker.state === 'activated') announce()
    })
  }

  const check = (registration: ServiceWorkerRegistration) => {
    const now = Date.now()
    if (now - lastCheckedAt < MIN_INTERVAL_MS) return
    lastCheckedAt = now
    // 확인이 서지 못하면 조용히 넘어간다. 사용자가 청하지 않은 일의 실패를 화면에 올리지 않는다.
    void registration.update().catch(() => {})
  }

  let registration: ServiceWorkerRegistration | null = null

  const onVisible = () => {
    if (document.visibilityState !== 'visible' || !registration) return
    check(registration)
  }

  void navigator.serviceWorker.ready
    .then((ready) => {
      if (stopped) return
      registration = ready

      // 이미 기다리고 있는 워커가 있으면 이 창은 그것을 만나기 전에 뜬 것이다.
      watchWorker(ready.waiting)
      ready.addEventListener('updatefound', () => watchWorker(ready.installing))

      // 화면을 처음 그린 뒤 한 번, 그리고 창이 다시 앞으로 나올 때마다 한 번 확인한다.
      check(ready)
      document.addEventListener('visibilitychange', onVisible)
    })
    .catch(() => {})

  return () => {
    stopped = true
    document.removeEventListener('visibilitychange', onVisible)
  }
}

/**
 * 화면을 다시 세워 새 자산으로 갈아 끼운다.
 *
 * 화면 안에서 경로만 바꾸지 않고 문서를 통째로 다시 받는다. 갈아 끼울 대상이 지금 돌고 있는
 * 스크립트 자체라 그것을 들고 있는 채로는 바뀌지 않는다.
 */
export function reopenWithNewAssets(): void {
  window.location.reload()
}
